/**
 * SESSION 146ar — DhanTokensTab
 *
 * Admin UI to manage 1 Dhan client_id + 4 purpose-specific access tokens
 * (NIFTY / BANKNIFTY / SENSEX / VCP). Each token gets its own Dhan
 * rate-limit bucket so live engines don't contend with the heavier
 * VCP scoring runs.
 *
 * Tokens are stored encrypted (Fernet) on the backend; this tab
 * never receives the raw token after save — only a masked preview.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  CheckCircle2, AlertTriangle, XCircle, Eye, EyeOff,
  Loader2, RefreshCw, Save, Shield, Clock, KeyRound,
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const PURPOSES = [
  {
    key: 'nifty',
    label: 'NIFTY',
    blurb: 'Live ticks + OHLC for NIFTY 50 index engine.',
    accent: 'sky',
  },
  {
    key: 'banknifty',
    label: 'BANKNIFTY',
    blurb: 'Live ticks + OHLC for Bank Nifty engine.',
    accent: 'amber',
  },
  {
    key: 'sensex',
    label: 'SENSEX',
    blurb: 'Live ticks + OHLC for Sensex engine.',
    accent: 'rose',
  },
  {
    key: 'vcp',
    label: 'VCP',
    blurb: 'DhanSwing + OI-VWAP — Nifty 200 OHLC + futures intraday OI.',
    accent: 'violet',
  },
];

const accentClass = (a, kind) => ({
  sky: { bg: 'bg-sky-500/15', ring: 'ring-sky-500/40', text: 'text-sky-300', border: 'border-sky-700/50' },
  amber: { bg: 'bg-amber-500/15', ring: 'ring-amber-500/40', text: 'text-amber-300', border: 'border-amber-700/50' },
  rose: { bg: 'bg-rose-500/15', ring: 'ring-rose-500/40', text: 'text-rose-300', border: 'border-rose-700/50' },
  violet: { bg: 'bg-violet-500/15', ring: 'ring-violet-500/40', text: 'text-violet-300', border: 'border-violet-700/50' },
}[a][kind]);

const formatExp = (secs) => {
  if (secs == null) return '—';
  if (secs < 0) return 'expired';
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  if (h > 24) return `${Math.floor(h / 24)}d ${h % 24}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
};

const validityIcon = (info) => {
  if (info?.valid === true) return <CheckCircle2 size={14} className="text-emerald-400" />;
  if (info?.valid === false) return <XCircle size={14} className="text-rose-400" />;
  if (info?.present) return <AlertTriangle size={14} className="text-amber-400" />;
  return <Clock size={14} className="text-zinc-500" />;
};

const TokenCard = ({ purpose, info, value, onChange, show, onToggleShow }) => {
  const a = purpose.accent;
  const expSecs = info?.expires_in_seconds;
  const expClass =
    expSecs == null ? 'text-zinc-500'
      : expSecs < 0 ? 'text-rose-400'
      : expSecs < 7200 ? 'text-amber-400'
      : 'text-emerald-400';

  return (
    <div
      className={`rounded-xl border ${accentClass(a, 'border')} bg-zinc-950/60 p-4 space-y-3`}
      data-testid={`dhan-token-card-${purpose.key}`}
    >
      <div className="flex items-start justify-between">
        <div>
          <div className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-semibold ring-1 ${accentClass(a, 'bg')} ${accentClass(a, 'ring')} ${accentClass(a, 'text')}`}>
            <KeyRound size={10} /> {purpose.label}
          </div>
          <p className="text-xs text-zinc-400 mt-1.5">{purpose.blurb}</p>
        </div>
        <div className="flex items-center gap-1.5 text-xs">
          {validityIcon(info)}
          <span className="text-zinc-400">
            {info?.present ? (info?.valid === true ? 'Live' : info?.valid === false ? 'Invalid' : 'Saved') : 'Empty'}
          </span>
        </div>
      </div>

      <div className="relative">
        <input
          type={show ? 'text' : 'password'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={info?.masked ? `Saved: ${info.masked}` : 'Paste fresh JWT here'}
          className="w-full bg-zinc-900 border border-zinc-700 rounded-md px-3 py-2 pr-9 text-xs font-mono text-zinc-100 placeholder-zinc-500 focus:outline-none focus:ring-1 focus:ring-violet-500"
          data-testid={`dhan-token-input-${purpose.key}`}
        />
        <button
          type="button"
          onClick={onToggleShow}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
          data-testid={`dhan-token-toggle-${purpose.key}`}
          tabIndex={-1}
        >
          {show ? <EyeOff size={14} /> : <Eye size={14} />}
        </button>
      </div>

      <div className="flex items-center justify-between text-[11px] text-zinc-500 font-mono">
        <span>
          Expires in <span className={expClass}>{formatExp(expSecs)}</span>
        </span>
        {info?.last_validated && (
          <span className="text-zinc-600 truncate">
            Validated: {new Date(info.last_validated).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', hour12: false })}
          </span>
        )}
      </div>

      {info?.validation_message && (
        <div className={`text-[11px] truncate ${info?.valid ? 'text-emerald-400' : 'text-rose-400'}`} data-testid={`dhan-token-msg-${purpose.key}`}>
          {info.validation_message}
        </div>
      )}
    </div>
  );
};

const DhanTokensTab = () => {
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [revalidating, setRevalidating] = useState(false);
  const [clientId, setClientId] = useState('');
  const [tokens, setTokens] = useState({ nifty: '', banknifty: '', sensex: '', vcp: '' });
  const [show, setShow] = useState({ nifty: false, banknifty: false, sensex: false, vcp: false });
  const [validateOnSave, setValidateOnSave] = useState(true);

  const getAuth = useCallback(() => {
    const t = localStorage.getItem('token');
    return t ? { Authorization: `Bearer ${t}` } : {};
  }, []);

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      const r = await axios.get(`${API}/api/admin/dhan-tokens`, { headers: getAuth() });
      setSnapshot(r.data);
      if (r.data?.client_id) setClientId(r.data.client_id);
    } catch (e) {
      toast.error(`Load failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setLoading(false);
    }
  }, [getAuth]);

  useEffect(() => { refresh(); }, [refresh]);

  const saveAll = async () => {
    if (!clientId.trim()) {
      toast.error('Client ID is required.');
      return;
    }
    if (!/^\d+$/.test(clientId.trim())) {
      toast.error('Client ID must be numeric (Dhan dhanClientId).');
      return;
    }
    const anyProvided = Object.values(tokens).some((v) => v && v.trim());
    if (!anyProvided && !window.confirm('No new tokens entered — only client ID will be updated. Continue?')) return;

    try {
      setSaving(true);
      const r = await axios.post(
        `${API}/api/admin/dhan-tokens`,
        { client_id: clientId.trim(), ...tokens, validate_on_save: validateOnSave },
        { headers: getAuth() },
      );
      setSnapshot(r.data);
      // Clear input fields so masked values from snapshot are visible.
      setTokens({ nifty: '', banknifty: '', sensex: '', vcp: '' });
      const vresults = r.data?.validation_results || {};
      const okCount = Object.values(vresults).filter((v) => v.valid).length;
      const failCount = Object.values(vresults).filter((v) => v.valid === false).length;
      if (validateOnSave && (okCount + failCount) > 0) {
        if (failCount === 0) {
          toast.success(`Saved · ${okCount} token(s) validated against Dhan.`);
        } else {
          toast.warning(`Saved · ${okCount} valid, ${failCount} invalid. Check messages.`);
        }
      } else {
        toast.success('Tokens saved.');
      }
    } catch (e) {
      toast.error(`Save failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setSaving(false);
    }
  };

  const revalidate = async () => {
    try {
      setRevalidating(true);
      const r = await axios.post(`${API}/api/admin/dhan-tokens/validate`, {}, { headers: getAuth() });
      setSnapshot(r.data?.snapshot || snapshot);
      const results = r.data?.results || {};
      const ok = Object.values(results).filter((v) => v.valid).length;
      const total = Object.keys(results).length;
      toast.success(`Re-validated ${ok}/${total} tokens against Dhan.`);
    } catch (e) {
      toast.error(`Re-validate failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setRevalidating(false);
    }
  };

  const summary = useMemo(() => {
    const t = snapshot?.tokens || {};
    return {
      configured: PURPOSES.filter((p) => t[p.key]?.present).length,
      valid: PURPOSES.filter((p) => t[p.key]?.valid === true).length,
      expiring: PURPOSES.filter((p) => {
        const s = t[p.key]?.expires_in_seconds;
        return s != null && s > 0 && s < 7200;
      }).length,
      expired: PURPOSES.filter((p) => {
        const s = t[p.key]?.expires_in_seconds;
        return s != null && s < 0;
      }).length,
    };
  }, [snapshot]);

  return (
    <div className="space-y-5 px-2 md:px-4 pb-12" data-testid="dhan-tokens-tab">
      <header className="flex items-start justify-between gap-3 border-b border-zinc-800 pb-4">
        <div>
          <h1 className="text-base font-semibold text-zinc-100 flex items-center gap-2">
            <Shield size={16} className="text-violet-300" />
            Dhan Tokens
          </h1>
          <p className="text-xs text-zinc-400 mt-1 max-w-2xl">
            One Dhan account, four purpose-partitioned access tokens. Each token gets its own
            5 req/sec rate-limit bucket so live <code className="text-violet-300">NIFTY/BANKNIFTY/SENSEX</code> engines never
            contend with the heavier <code className="text-violet-300">VCP</code> scoring runs.
            Tokens are encrypted at rest and never returned in plaintext after save.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={refresh}
            disabled={loading}
            className="rounded-md bg-zinc-800 hover:bg-zinc-700 px-3 py-1.5 text-xs text-zinc-200 inline-flex items-center gap-1 disabled:opacity-50"
            data-testid="dhan-tokens-refresh-btn"
          >
            {loading ? <Loader2 className="animate-spin" size={12} /> : <RefreshCw size={12} />}
            Refresh
          </button>
          <button
            onClick={revalidate}
            disabled={revalidating || !snapshot?.configured}
            className="rounded-md bg-emerald-700 hover:bg-emerald-600 px-3 py-1.5 text-xs text-white inline-flex items-center gap-1 disabled:opacity-50"
            data-testid="dhan-tokens-revalidate-btn"
          >
            {revalidating ? <Loader2 className="animate-spin" size={12} /> : <CheckCircle2 size={12} />}
            Re-validate all
          </button>
        </div>
      </header>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <SummaryStat label="Configured" value={summary.configured} testId="dhan-summary-configured" />
        <SummaryStat label="Valid" value={summary.valid} tone="success" testId="dhan-summary-valid" />
        <SummaryStat label="Expiring <2h" value={summary.expiring} tone={summary.expiring > 0 ? 'warn' : 'neutral'} testId="dhan-summary-expiring" />
        <SummaryStat label="Expired" value={summary.expired} tone={summary.expired > 0 ? 'danger' : 'neutral'} testId="dhan-summary-expired" />
      </div>

      <section className="rounded-xl border border-zinc-800 bg-zinc-950/60 p-5 space-y-4">
        <div className="grid md:grid-cols-2 gap-4">
          <label className="text-xs text-zinc-400 flex flex-col gap-1.5">
            Dhan Client ID
            <input
              value={clientId}
              onChange={(e) => setClientId(e.target.value)}
              placeholder="e.g. 1106638717"
              className="bg-zinc-900 border border-zinc-700 rounded-md px-3 py-2 text-sm font-mono text-zinc-100 placeholder-zinc-500 focus:outline-none focus:ring-1 focus:ring-violet-500"
              data-testid="dhan-client-id-input"
            />
            <span className="text-[11px] text-zinc-500">
              Find this in your Dhan app · Profile → Account · same for all 4 tokens.
            </span>
          </label>
          <div className="flex flex-col justify-end">
            <label className="inline-flex items-center gap-2 text-xs text-zinc-400">
              <input
                type="checkbox"
                checked={validateOnSave}
                onChange={(e) => setValidateOnSave(e.target.checked)}
                data-testid="dhan-validate-on-save-toggle"
              />
              Validate against Dhan profile API on save (~5s).
            </label>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-3">
          {PURPOSES.map((p) => (
            <TokenCard
              key={p.key}
              purpose={p}
              info={snapshot?.tokens?.[p.key]}
              value={tokens[p.key]}
              onChange={(v) => setTokens((prev) => ({ ...prev, [p.key]: v }))}
              show={show[p.key]}
              onToggleShow={() => setShow((prev) => ({ ...prev, [p.key]: !prev[p.key] }))}
            />
          ))}
        </div>

        <div className="flex items-center justify-between pt-2 border-t border-zinc-800">
          <p className="text-[11px] text-zinc-500">
            Generate fresh tokens at <a href="https://dhan.co/developer" target="_blank" rel="noreferrer" className="text-violet-300 hover:underline">dhan.co/developer</a> · Tokens last 24h · Empty fields are skipped (existing values preserved).
          </p>
          <button
            onClick={saveAll}
            disabled={saving || !clientId.trim()}
            className="rounded-md bg-violet-600 hover:bg-violet-500 px-4 py-2 text-xs text-white inline-flex items-center gap-1.5 disabled:opacity-50 font-semibold"
            data-testid="dhan-tokens-save-btn"
          >
            {saving ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />}
            Save All
          </button>
        </div>
      </section>
    </div>
  );
};

const SummaryStat = ({ label, value, tone = 'neutral', testId }) => {
  const toneCls = {
    success: 'text-emerald-300',
    warn: 'text-amber-300',
    danger: 'text-rose-300',
    neutral: 'text-zinc-200',
  }[tone];
  return (
    <div className="rounded-md bg-zinc-900 border border-zinc-800 px-3 py-2.5" data-testid={testId}>
      <div className="text-[10px] uppercase text-zinc-500 tracking-wider">{label}</div>
      <div className={`text-lg font-bold mt-0.5 ${toneCls}`}>{value}</div>
    </div>
  );
};

export default DhanTokensTab;
